import * as Sentry from '@sentry/react';
import { environment } from '../../environment.ts';
import { WebSocketStatus, CodeToCategory, WSEventCategory } from './types';

const MAX_RETRY = 20;
const MAX_WAIT = 60_000;
const RETRY_DELAYS = [0, 60, 250, 500, 1_000, 2_000, 4_000, 8_000, 16_000, 32_000];
const MIN_STABLE_CONNECTION_DURATION = 5_000;

interface WebSocketConfig {
  url: string;
  onStatusChange: (status: WebSocketStatus) => void;
  onMessage: (event: MessageEvent) => void;
  onError?: (category: WSEventCategory, code: number, reason: string) => void;
}

export class WebSocketConnection {
  private ws: WebSocket | null = null;
  private status: WebSocketStatus = 'CLOSED';
  private retryCount = 0;
  private retryTimeoutId?: NodeJS.Timeout;
  private resetRetryCountTimeoutId?: NodeJS.Timeout;
  private isManuallyDisconnected = false;

  constructor(private config: WebSocketConfig) {}

  private _reportError(error: unknown, context: Record<string, unknown> = {}) {
    const extra = { ...context, url: this.config.url };
    if (environment.production) {
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(errorToCapture, { extra });
    } else {
      console.error('WebSocket error:', error, extra);
    }
  }

  private setStatus(newStatus: WebSocketStatus) {
    if (this.status !== newStatus) {
      this.status = newStatus;
      this.config.onStatusChange(newStatus);
    }
  }

  private getRetryDelay(): number {
    return RETRY_DELAYS[this.retryCount] ?? MAX_WAIT;
  }

  private clearTimers() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = undefined;
    }
    if (this.resetRetryCountTimeoutId) {
      clearTimeout(this.resetRetryCountTimeoutId);
      this.resetRetryCountTimeoutId = undefined;
    }
  }

  private closeSocketSilently(reason: string) {
    if (!this.ws) return;
    try {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close(1000, reason);
    } catch (err) {
      this._reportError(err, { context: 'WebSocket close failed' });
    } finally {
      this.ws = null;
    }
  }

  private shouldRetry(event: CloseEvent): boolean {
    if (this.isManuallyDisconnected) return false;
    if (this.retryCount >= MAX_RETRY) return false;

    return CodeToCategory[event.code] === 'retry';
  }

  private handleCloseEvent(event: CloseEvent) {
    const category = CodeToCategory[event.code] || 'error';

    if (this.config.onError) {
      this.config.onError(category, event.code, event.reason);
    } else {
      console.warn('[WebSocket] Connection closed:', {
        code: event.code,
        reason: event.reason,
        category,
        url: this.config.url,
        event,
      });
    }

    this.setStatus('CLOSED');
    this.ws = null;

    if (this.shouldRetry(event)) {
      this.scheduleReconnect();
    } else if (!this.isManuallyDisconnected) {
      this.setStatus('ERROR');
      console.warn('[WebSocket] Connection closed');
    }
  }

  private scheduleReconnect() {
    this.clearTimers();

    const delay = this.getRetryDelay();
    this.retryCount++;
    this.retryTimeoutId = setTimeout(() => {
      this.connect();
    }, delay);
  }

  forceReconnect(): void {
    this.isManuallyDisconnected = false;
    this.clearTimers();
    this.closeSocketSilently('Force reconnect');
    this.retryCount = 0;
    this.setStatus('CLOSED');
    this.connect();
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.CONNECTING || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isManuallyDisconnected = false;
    this.setStatus('CONNECTING');

    try {
      this.ws = new WebSocket(this.config.url);

      this.ws.onopen = () => {
        this.setStatus('OPEN');
        clearTimeout(this.resetRetryCountTimeoutId);
        this.resetRetryCountTimeoutId = setTimeout(() => {
          if (this.status === 'OPEN') {
            this.retryCount = 0;
          }
        }, MIN_STABLE_CONNECTION_DURATION);
      };

      this.ws.onmessage = this.config.onMessage;

      this.ws.onclose = (event) => {
        this.handleCloseEvent(event);
      };
    } catch (error) {
      this.setStatus('ERROR');
      this._reportError(error, { context: 'Failed to create WebSocket' });
      if (this.config.onError) {
        this.config.onError('error', 0, 'Failed to create WebSocket');
      }
    }
  }

  disconnect(): void {
    this.isManuallyDisconnected = true;
    this.clearTimers();

    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      try {
        this.ws.close(1000, 'Manual disconnect');
      } catch (err) {
        this._reportError(err, { context: 'Manual disconnect failed' });
      }
    }
  }

  send(data: string): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(data);
        return true;
      } catch (err) {
        this._reportError(err, { context: 'WebSocket send failed' });
        return false;
      }
    }
    return false;
  }

  sendJSON<T>(data: T): boolean {
    try {
      return this.send(JSON.stringify(data));
    } catch (err) {
      this._reportError(err, { context: 'WebSocket sendJSON failed' });
      return false;
    }
  }

  getStatus(): WebSocketStatus {
    return this.status;
  }

  getRetryCount(): number {
    return this.retryCount;
  }
}
